import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { TypePolicyService } from 'src/app/service/typePolicy.service';
import { PolicyService } from 'src/app/service/policy.service';
import { SearchParamsModel } from 'src/app/model/search-params.model';
import { AttributeModel } from 'src/app/main/dynamic/model/attribute.model';
import { MotorPolicyService } from 'src/app/service/motor-policy.service';
import { PolicyModel } from 'src/app/model/policy.model';
import { DynamicEvent } from '../../dynamic/dynamic.event';

@Component({
  selector: 'app-dashboard-filter',
  templateUrl: './dashboard-filter.component.html',
  styleUrls: ['./dashboard-filter.component.scss']
})
export class DashboardFilterComponent implements OnInit {
  @Input() searchParams: SearchParamsModel;
  @Output() search = new EventEmitter<void>();

  private readonly searchParamsKey = "searchParams_start";
  private filterPolicyId: string;
  filters: AttributeModel[] = [];
  showAdvancedSearch = false;

  constructor(
    private policyService: PolicyService,
    private typePolicyService: TypePolicyService,
    private motorPolicyService: MotorPolicyService
  ) {}

  async ngOnInit() {
    this.showAdvancedSearch = false;
    await this.loadFilters();
    this.loadSearchParams();

    for (const filter of this.filters) {
      if (filter.value) {
        await this.updateFiltersOnChange(filter);
      }
    }
  }

  private async loadFilters(): Promise<void> {
    const typePolicies = await this.typePolicyService.list().toPromise();
    const type = typePolicies.find(t => t.options.some(opt => opt.key.toLowerCase() === 'type' && opt.value.toLowerCase() === 'filter'));

    if (type) {
      const policies = await this.policyService.listByTypePolicy(type.id).toPromise();
      if (policies && policies.length > 0) {
        this.filterPolicyId = policies[0].id;
        const policyResponse = await this.motorPolicyService.getBy(policies[0].id).toPromise();
        
        if (policyResponse) {
          const filterAttribute = policyResponse.attributes.find(attr => attr.key.toLowerCase() == "start");
          if (filterAttribute) {
            this.filters = filterAttribute.attributes;
          }
        }
      }
    }
  }

  private async updateFiltersOnChange(filter: AttributeModel): Promise<void> {
    const shouldUpdate = filter.options?.some(opt => 
        opt.key.toLowerCase() === "perform front-end update" && 
        opt.value.toLowerCase() === "true"
    );

    if (shouldUpdate && filter.value) {
        const updateDataField = filter.options?.find(opt => 
            opt.key.toLowerCase() === "update data field"
        );

        if (updateDataField) {
            const updateAttribute = {
                key: updateDataField.value,
                value: filter.value,
                type: "Text"
            };

            try {
                const queryParams = `?attributes[0].key=${updateAttribute.key}&attributes[0].value=${updateAttribute.value}&attributes[0].type=${updateAttribute.type}`;
                const updatedPolicy = await this.motorPolicyService
                    .getWithComplement<PolicyModel>(`${this.filterPolicyId}${queryParams}`)
                    .toPromise();

                if (updatedPolicy) {
                    const filterAttribute = updatedPolicy.attributes.find(attr => attr.key.toLowerCase() == "start");
                    if (filterAttribute) {
                        const currentValue = filter.value;
                        const prevSelectedMeta = filter.attributes?.find(a => a.type?.toLowerCase() === 'filterselectedoption');

                        this.filters = filterAttribute.attributes;
                        
                        const updatedFilter = this.filters.find(f => f.key === filter.key);
                        if (updatedFilter) {
                            updatedFilter.value = currentValue;

                            if (prevSelectedMeta) {
                                let meta = updatedFilter.attributes?.find(a => a.type?.toLowerCase() === 'filterselectedoption');
                                if (!meta) {
                                    if (!updatedFilter.attributes) { (updatedFilter as any).attributes = []; }
                                    updatedFilter.attributes.push({
                                        key: prevSelectedMeta.key,
                                        value: prevSelectedMeta.value,
                                        type: prevSelectedMeta.type,
                                        attributes: [...(prevSelectedMeta.attributes || [])],
                                        options: [...(prevSelectedMeta.options || [])]
                                    } as any);
                                } else {
                                    meta.value = prevSelectedMeta.value;
                                    meta.attributes = [...(prevSelectedMeta.attributes || [])];
                                    meta.options = [...(prevSelectedMeta.options || [])];
                                }
                            }
                        }
                    }
                }
            } catch (error) {
                console.error('Erro ao atualizar filtros:', error);
            }
        }
    }
  }

  private loadSearchParams() {  
    if (!this.searchParamsKey) {
      console.error('Unable to load search params. Key is invalid or missing.');
      return;
    }
  
    if (sessionStorage.getItem(this.searchParamsKey)) {
      this.searchParams = JSON.parse(sessionStorage.getItem(this.searchParamsKey));
    }
  
    Object.keys(this.searchParams).forEach(key => {
      if (!Array.isArray(this.searchParams[key])) {
        this.setParamIntoFilterByFieldName(key, this.searchParams[key]);
      }
    });
  
    if (this.searchParams.attributes) {
      this.searchParams.attributes.forEach(attr => {
        this.setParamIntoFilterByAttribute(attr);
      });
    }
  }

  private setParamIntoFilterByFieldName(fieldName: string, value: string) {
    let filter = this.filters.find(f =>
      f.options.some(opt => opt.key == "searchField" && opt.value == fieldName))

      if(filter && value){
        let binding = filter.attributes.find(attr => attr.key.toLocaleLowerCase() == 'binding')
        if(binding.value || this.isValueInBindingList(binding, value)) {
          filter.value = value;
          this.showAdvancedSearch = true;
        }else{
          this.searchParams[fieldName] = ''
          sessionStorage.setItem(this.searchParamsKey, JSON.stringify(this.searchParams));
          this.search.emit()
        }
      }
  }

  private isValueInBindingList(binding: AttributeModel, value: string):boolean {
    if (value == null) {
      return false;
    }

    return binding.attributes.some(opt => opt.value.toLocaleLowerCase() == value?.toLocaleLowerCase());
  }

  private setParamIntoFilterByAttribute(attribute: AttributeModel) {
    let filter = this.filters.find(f => f.key == attribute.key)

    if(filter) {
      const binding = filter.attributes.find(attr => attr.key.toLocaleLowerCase() == 'binding');
      if (!binding || binding.value || this.isValueInBindingList(binding, attribute.value)) {
        filter.value = attribute.value;

        if (filter.value) {
          this.showAdvancedSearch = true;
        }
      } else {
        filter.value = null;
      }
    }
  }

  private saveSearchParams() {
    sessionStorage.setItem(this.searchParamsKey, JSON.stringify(this.searchParams));
  }

  doSearch() {
    this.buildSearchParams();
    this.saveSearchParams();
    this.search.emit();
  }

  private buildSearchParams() {
    this.searchParams.attributes = [];
    this.filters.forEach(filter => {
      const filterPath = filter.attributes.find(attr => attr.type === "filterPath");
      if (filterPath) {
        const searchField = filter.options.find(opt => opt.key === "searchField");
        const binding = filter.attributes.find(attr => attr.key?.toLowerCase() === 'binding' || attr.type?.toLowerCase() === 'binding');
        const bindingOptions = binding?.attributes || [];
        const selectedOptionMeta = filter.attributes.find(attr => attr.type?.toLowerCase() === 'filterselectedoption');

        let selectedKey = filter.value;
        if (selectedKey == null || selectedKey === '') {
          selectedKey = null;
        } else if (selectedOptionMeta && selectedOptionMeta.attributes?.length) {
          selectedKey = selectedOptionMeta.attributes[0].key || null;
        } else if (bindingOptions.length) {
          const selected = bindingOptions.find(o => o.key == filter.value) || bindingOptions.find(o => (o.value || '').toLowerCase() === String(filter.value).toLowerCase());
          selectedKey = selected ? selected.key : null;
        } else {
          selectedKey = filter.value;
        }
        filterPath.attributes.forEach(o => o.value = selectedKey);

        if (searchField) {
          if (selectedKey != null && selectedKey !== '') {
            this.searchParams[searchField.value] = selectedKey;
          } else {
            this.searchParams[searchField.value] = '';
          }
        } else {
          this.searchParams.attributes.push(filter);
        }
      }
    });
  }

  async handleFilterChange(event: DynamicEvent[]) {
    const filter = event[0].attribute;
    if (filter) {
      await this.updateFiltersOnChange(filter);
    }
    this.doSearch();
  }

  toggleAdvancedSearch(): void {
    this.showAdvancedSearch = !this.showAdvancedSearch;
  }
}